import {
  PLANNED_ABSENCE_DEADLINE_HOURS,
  addDays,
  formatDateRu,
  isPlannedAbsenceAllowed,
  shiftStartUtc,
} from '@pms/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuthStore } from '../auth/store.js';
import { db } from '../sync/db.js';
import { enqueue } from '../sync/outbox.js';

/**
 * Невыход на смену. Два разных пути, и объединять их нельзя.
 *
 * Плановое отсутствие подают заранее — позже чем за 48 часов форма закрывается.
 * Экстренное уведомление доступно всегда: человек не заболевает по расписанию,
 * и если закрыть этот путь, данные о невыходах просто перестанут попадать в систему.
 */
export function AbsenceScreen(): JSX.Element {
  const user = useAuthStore((state) => state.user);
  const [mode, setMode] = useState<'planned' | 'unplanned'>('planned');
  const [siteId, setSiteId] = useState<string | null>(null);
  const [date, setDate] = useState<string>(addDays(new Date().toISOString().slice(0, 10), 3));
  const [shiftNumber, setShiftNumber] = useState(1);
  const [reason, setReason] = useState('');
  const [sent, setSent] = useState(false);

  /**
   * Мастер и администратор записывают невыход за человека: сам работник эту
   * кнопку больше не видит. Оператору выбор сотрудника не показывается —
   * ему записывать не за кого.
   */
  const forOthers = user?.role === 'MASTER' || user?.role === 'ADMIN';
  const [subjectId, setSubjectId] = useState<string | null>(null);
  const operators = useLiveQuery(
    async () =>
      forOthers
        ? (await db.users.where('role').equals('OPERATOR').toArray())
            .filter((person) => person.isActive)
            .sort((a, b) => a.fullName.localeCompare(b.fullName, 'ru'))
        : [],
    [forOthers],
    [],
  );

  const sites = useLiveQuery(() => db.sites.orderBy('sortOrder').toArray(), [], []);
  const templates = useLiveQuery(
    async () => (siteId ? await db.shiftTemplates.where('siteId').equals(siteId).toArray() : []),
    [siteId],
    [],
  );

  const template = templates.find(
    (item) => item.shiftNumber === shiftNumber && item.validTo === null,
  );

  const shiftStart = template
    ? shiftStartUtc(date, {
        shiftNumber: template.shiftNumber,
        startMinute: template.startMinute,
        durationMinutes: template.durationMinutes,
      })
    : null;

  const tooLate =
    mode === 'planned' && shiftStart !== null && !isPlannedAbsenceAllowed(new Date(), shiftStart);

  const canSend =
    Boolean(siteId) && reason.trim().length >= 3 && !tooLate && (!forOthers || subjectId !== null);

  const submit = async (): Promise<void> => {
    if (!user || !siteId) return;
    if (forOthers && subjectId === null) return;
    await enqueue({
      type: mode === 'unplanned' ? 'ABSENCE_REPORTED_UNPLANNED' : 'ABSENCE_REQUESTED',
      aggregateType: 'ABSENCE',
      aggregateId: crypto.randomUUID(),
      userId: user.id,
      payload: {
        // За себя поле не отправляем: пустое означает «за себя»
        ...(forOthers && subjectId !== null ? { userId: subjectId } : {}),
        siteId,
        date,
        shiftNumber,
        reason: reason.trim(),
        unplanned: mode === 'unplanned',
      },
    });
    if (navigator.vibrate) navigator.vibrate([30, 50, 30]);
    setSent(true);
  };

  const dateOptions = Array.from({ length: 10 }, (_, index) =>
    addDays(new Date().toISOString().slice(0, 10), index),
  );

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">
          {forOthers ? 'Записать невыход' : 'Не выйду на смену'}
        </h1>
        <Link to="/" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      <div className="mb-6 flex flex-col gap-touch">
        <button
          type="button"
          onClick={() => {
            setMode('planned');
            setSent(false);
          }}
          className={`min-h-touch rounded-xl px-5 py-4 text-left text-lg font-semibold ${
            mode === 'planned' ? 'bg-sky-700 text-white' : 'bg-card shadow-card'
          }`}
        >
          Плановое отсутствие
          <span className="block text-base font-normal text-body">
            Заранее, не позже чем за {PLANNED_ABSENCE_DEADLINE_HOURS} часов
          </span>
        </button>
        <button
          type="button"
          onClick={() => {
            setMode('unplanned');
            setSent(false);
          }}
          className={`min-h-touch rounded-xl px-5 py-4 text-left text-lg font-semibold ${
            mode === 'unplanned' ? 'bg-amber-500 text-black' : 'bg-card shadow-card'
          }`}
        >
          Болезнь или форс-мажор
          <span className="block text-base font-normal opacity-80">
            Доступно всегда, уходит администратору сразу
          </span>
        </button>
      </div>

      {forOthers && (
        <section className="mb-4">
          <h2 className="mb-3 text-lg font-semibold">Кто не вышел</h2>
          <div className="flex flex-col gap-touch">
            {operators.map((person) => (
              <button
                key={person.id}
                type="button"
                onClick={() => {
                  setSubjectId(person.id);
                  setSent(false);
                }}
                className={`min-h-touch rounded-xl px-5 py-4 text-left text-lg font-semibold ${
                  person.id === subjectId ? 'bg-sky-700 text-white' : 'bg-card shadow-card'
                }`}
              >
                {person.fullName}
              </button>
            ))}
            {operators.length === 0 && (
              <p className="rounded-xl bg-card shadow-card p-4 text-base text-dim">
                Операторов в справочнике нет.
              </p>
            )}
          </div>
        </section>
      )}

      <section className="mb-4">
        <h2 className="mb-3 text-lg font-semibold">Участок</h2>
        <div className="flex flex-col gap-touch">
          {sites.map((site) => (
            <button
              key={site.id}
              type="button"
              onClick={() => setSiteId(site.id)}
              className={`min-h-touch rounded-xl px-5 py-4 text-left text-lg font-semibold ${
                site.id === siteId ? 'bg-sky-700 text-white' : 'bg-card shadow-card'
              }`}
            >
              {site.name}
            </button>
          ))}
        </div>
      </section>

      <section className="mb-4">
        <h2 className="mb-3 text-lg font-semibold">Дата</h2>
        <div className="flex flex-wrap gap-touch">
          {dateOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setDate(option)}
              className={`min-h-touch rounded-xl px-4 text-base font-semibold ${
                option === date ? 'bg-sky-700 text-white' : 'bg-card shadow-card'
              }`}
            >
              {formatDateRu(option).slice(0, 5)}
            </button>
          ))}
        </div>
      </section>

      <section className="mb-4">
        <h2 className="mb-3 text-lg font-semibold">Смена</h2>
        <div className="flex gap-touch">
          {[1, 2, 3].map((number) => (
            <button
              key={number}
              type="button"
              onClick={() => setShiftNumber(number)}
              className={`min-h-touch flex-1 rounded-xl px-4 py-4 text-lg font-semibold ${
                number === shiftNumber ? 'bg-sky-700 text-white' : 'bg-card shadow-card'
              }`}
            >
              {number}
            </button>
          ))}
        </div>
      </section>

      <label className="mb-4 flex flex-col gap-2 text-base">
        Причина
        <textarea
          rows={2}
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
            setSent(false);
          }}
          className="rounded-xl bg-canvas px-4 py-3 text-lg outline-none focus:ring-2 focus:ring-sky-500"
        />
      </label>

      {tooLate && (
        <p className="mb-4 rounded-xl bg-amber-500 px-5 py-4 text-base text-black">
          До этой смены меньше {PLANNED_ABSENCE_DEADLINE_HOURS} часов — плановую заявку подать
          нельзя. Если это болезнь или форс-мажор, выберите «Болезнь или форс-мажор»: этот путь
          открыт всегда.
        </p>
      )}

      {sent ? (
        <p className="rounded-xl bg-emerald-800 px-5 py-4 text-lg font-semibold text-white">
          Отправлено администратору
        </p>
      ) : (
        <button
          type="button"
          disabled={!canSend}
          onClick={() => void submit()}
          className={`w-full rounded-xl px-5 py-5 text-lg font-semibold disabled:opacity-40 ${
            mode === 'unplanned'
              ? 'bg-amber-500 text-black active:bg-amber-600'
              : 'bg-sky-600 text-white active:bg-sky-700'
          }`}
        >
          {mode === 'unplanned' ? 'Сообщить срочно' : 'Отправить заявку'}
        </button>
      )}
    </div>
  );
}
